<script setup lang="ts">
import { getEffect } from '~/utils/effects'
import type { EffectContext } from '~/utils/effects/types'

const props = defineProps<{
  effect: string
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
let animationId: number | null = null
let state: unknown = null
let startTime = 0
let lastTime = 0

function getContext(): EffectContext | null {
  const canvas = canvasRef.value
  if (!canvas) return null
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const dpr = window.devicePixelRatio || 1
  return {
    canvas,
    ctx,
    width: canvas.clientWidth,
    height: canvas.clientHeight,
    dpr,
    time: 0,
    deltaTime: 0
  }
}

function resizeCanvas() {
  const canvas = canvasRef.value
  if (!canvas) return
  const dpr = window.devicePixelRatio || 1
  const rect = canvas.getBoundingClientRect()
  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr
  const ctx = canvas.getContext('2d')
  if (ctx) ctx.scale(dpr, dpr)

  // Reinitialize with new dimensions
  initEffect()
}

function initEffect() {
  const effectDef = getEffect(props.effect)
  const ctx = getContext()
  if (!effectDef || !ctx) {
    state = null
    return
  }
  state = effectDef.init(ctx)
  startTime = performance.now()
  lastTime = startTime
}

function loop(now: number) {
  const effectDef = getEffect(props.effect)
  const canvas = canvasRef.value
  if (!effectDef || !canvas || !state) {
    animationId = null
    return
  }

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const dpr = window.devicePixelRatio || 1
  const width = canvas.clientWidth
  const height = canvas.clientHeight

  const time = now - startTime
  const deltaTime = Math.min(now - lastTime, 50) // Cap at 50ms to prevent jumps
  lastTime = now

  const effectCtx: EffectContext = {
    canvas,
    ctx,
    width,
    height,
    dpr,
    time,
    deltaTime
  }

  ctx.clearRect(0, 0, width, height)
  effectDef.update(state, effectCtx)
  effectDef.draw(state, effectCtx)

  animationId = requestAnimationFrame(loop)
}

function startLoop() {
  stopLoop()
  if (props.effect && getEffect(props.effect)) {
    animationId = requestAnimationFrame(loop)
  }
}

function stopLoop() {
  if (animationId !== null) {
    cancelAnimationFrame(animationId)
    animationId = null
  }
}

function clearCanvas() {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (ctx) ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)
}

// Watch effect changes
watch(() => props.effect, () => {
  stopLoop()
  if (!props.effect || props.effect === 'none') {
    state = null
    clearCanvas()
    return
  }
  initEffect()
  startLoop()
})

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  resizeCanvas()

  resizeObserver = new ResizeObserver(() => {
    resizeCanvas()
  })
  if (canvasRef.value) {
    resizeObserver.observe(canvasRef.value)
  }

  if (props.effect) {
    startLoop()
  }
})

onUnmounted(() => {
  stopLoop()
  resizeObserver?.disconnect()
})
</script>

<template>
  <canvas
    v-show="effect && effect !== 'none'"
    ref="canvasRef"
    class="absolute inset-0 w-full h-full pointer-events-none"
  />
</template>
